import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import {
  withLatestFrom,
  exhaustMap,
  switchMap,
  map,
  share,
  catchError,
} from 'rxjs/operators';
import { ErrorsType } from '@toro/common/shared/enum/errors.type';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { DefaultResponse } from '@toro/common/shared/models/form/DefaultResponse';
import { RoutesTable } from '@toro/common/shared/resources/routes-table.model';
import { BackendService } from '@toro/common/shared/services/backend/backend.service';
import { ExchangeActions } from './exchange.action';
import { Observable, of } from 'rxjs';

@Injectable()
export class ExchangeEffects {
  public tradingTime$ = this.store.select(s => s.exchange.tradingTime);

  interationSuccess$ = createEffect(
    () => this.actions$.pipe(ofType(ExchangeActions.getTradingTimeSuccess)),
    { dispatch: false }
  );

  interationError$ = createEffect(
    () => this.actions$.pipe(ofType(ExchangeActions.getTradingTimeError)),
    { dispatch: false }
  );

  /**
   * Obtém as ordens do cliente
   * @method getClientOrders
   */
  getClientOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExchangeActions.getClientOrders),
      switchMap(() => {
        return this.dataService
          .get<DefaultResponse>('exchange/clientorders')
          .pipe(
            map(result => {
              if (result.hasValue) {
                return {
                  type: ExchangeActions.getClientOrdersSuccess,
                  payload: result.value
                };
              }
              if (result.hasError) {
                return {
                  type: ExchangeActions.getClientOrdersError,
                  payload: { type: ErrorsType.Error, value: result.messages }
                };
              }

              return {
                type: ExchangeActions.getClientOrdersError,
                payload: { type: ErrorsType.Error, value: result.messages }
              };
            }),
            catchError(error =>
              of({ type: ExchangeActions.getClientOrdersError, payload: error })
            )
          );
      }),
      share()
    )
  );

  /**
   * Encerra uma estratégia
   */
  closeStrategy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExchangeActions.closeStrategy),
      exhaustMap((action: any) => {
        return this.dataService
          .post<DefaultResponse>(
            RoutesTable.exchange.routes.closeStrategy.uri,
            { strategyId: action.payload.strategyId },
            action.payload.token
          )
          .pipe(
            map(result => {
              if (result.ok) {
                return {
                  type: ExchangeActions.closeStrategySuccess,
                  payload: result.value
                };
              }

              return {
                type: ExchangeActions.closeStrategyError,
                payload: result.messages
              };
            }),
            catchError(error =>
              of({ type: ExchangeActions.closeStrategyError, payload: error })
            )
          );
      }),
      share()
    )
  );

  /**
   * Encerra uma order
   */
  closeOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExchangeActions.closeOrder),
      switchMap((action: any) => {
        return this.dataService
          .post<DefaultResponse>(
            RoutesTable.exchange.routes.closeOrder.uri,
            { orderId: action.payload.orderId },
            action.payload.token
          )
          .pipe(
            map(result => {
              if (result.ok) {
                return {
                  type: ExchangeActions.closeOrderSuccess,
                  payload: result.value
                };
              }

              return {
                type: ExchangeActions.closeOrderError,
                payload: result.messages
              };
            }),
            catchError(error =>
              of({ type: ExchangeActions.closeOrderError, payload: error })
            )
          );
      }),
      share()
    )
  );

  /**
   * Encerra uma order
   */
  cancelOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExchangeActions.cancelOrder),
      switchMap((action: any) => {
        return this.dataService
          .post<DefaultResponse>(
            RoutesTable.exchange.routes.cancelOrder.uri,
            { orderId: action.payload.orderId },
            action.payload.token
          )
          .pipe(
            map(result => {
              if (result.ok) {
                return {
                  type: ExchangeActions.cancelOrderSuccess,
                  payload: result.value
                };
              }
              return {
                type: ExchangeActions.cancelOrderError,
                payload: result.messages
              };
            }),
            catchError(error =>
              of({ type: ExchangeActions.cancelOrderError, payload: error })
            )
          );
      }),
      share()
    )
  );

  /**
   * Encerra uma order
   */
  cancelStrategy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExchangeActions.cancelStrategy),
      switchMap((action: any) => {
        return this.dataService
          .post<DefaultResponse>(
            RoutesTable.exchange.routes.cancelStrategy.uri,
            { strategyId: action.payload.orderId },
            action.payload.token
          )
          .pipe(
            map(result => {
              if (result.ok) {
                return {
                  type: ExchangeActions.cancelStrategySuccess,
                  payload: result.value
                };
              }
              return {
                type: ExchangeActions.cancelStrategyError,
                payload: result.messages
              };
            }),
            catchError(error =>
              of({ type: ExchangeActions.cancelStrategyError, payload: error })
            )
          );
      }),
      share()
    )
  );

  /**
   * Requisita ou retorna da store os horários de trade e bolsa.
   */
  getTradingTime$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExchangeActions.getTradingTime),
      withLatestFrom(this.tradingTime$),
      switchMap(([, tradingTime]) => {
        if (tradingTime) {
          return of({
            type: ExchangeActions.getTradingTimeSuccess,
            payload: tradingTime
          });
        }

        return this.dataService
          .get<DefaultResponse>(RoutesTable.exchange.routes.getTradingTime.uri)
          .pipe(
            map(result => {
              if (result.ok) {
                return {
                  type: ExchangeActions.getTradingTimeSuccess,
                  payload: result.value
                };
              }

              return {
                type: ExchangeActions.getTradingTimeError,
                payload: result.messages
              };
            }),
            catchError(error =>
              of({ type: ExchangeActions.getTradingTimeError, payload: error })
            )
          );
      }),
      share()
    )
  );

  constructor(
    private actions$: Actions<ExchangeActions>,
    private dataService: BackendService,
    private store: Store<AppState>
  ) {}
}